import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import { FaShoppingCart } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import routeRegistry from '../services/routeRegistry';
import { routeIcons, routeLabels } from '../constants/routes';
import { t } from '../i18n';

const GlobalSearchDropdown = ({ placeholder = 'Search...', onProductSearch, className = '' }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const { userRoles, isSuperuser, hasPagePermission } = useContext(AuthContext);

    const getAccessiblePages = useCallback(() => {
        const roleNames = Array.isArray(userRoles) ? userRoles : [];
        const effectiveRoles = isSuperuser ? [...roleNames, 'admin'] : roleNames;
        const routes = routeRegistry.getAccessibleRoutes(effectiveRoles)
            .filter(r => ['/', '/products'].includes(r.path) || hasPagePermission(r.path));
        return routes.map(r => ({
            path: r.path,
            label: t(routeLabels[r.path] || r.text || r.path),
            icon: routeIcons[r.path] || null,
            type: 'page'
        }));
    }, [userRoles, isSuperuser, hasPagePermission]);

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            setShowDropdown(false);
            return;
        }

        const searchTerms = query.toLowerCase().split(/\s+/).filter(term => term.length > 0);
        const pages = getAccessiblePages();

        const matched = pages.filter(page => {
            const text = page.label.toLowerCase();
            return searchTerms.every(term => text.includes(term));
        }).slice(0, 8);

        const hasProductAccess = onProductSearch != null;
        const productEntry = hasProductAccess && query.trim().length >= 2 ? [{
            path: `/products?search=${encodeURIComponent(query.trim())}`,
            label: `Search products for "${query.trim()}"`,
            icon: FaShoppingCart,
            type: 'product_search'
        }] : [];

        setResults([...matched, ...productEntry]);
        setShowDropdown(true);
        setSelectedIndex(-1);
    }, [query, getAccessiblePages, onProductSearch]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                inputRef.current && !inputRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (item) => {
        if (item.type === 'product_search') {
            if (onProductSearch) {
                onProductSearch(query.trim());
            }
            navigate(`/products?search=${encodeURIComponent(query.trim())}`);
        } else {
            navigate(item.path === '/' ? '/products' : item.path);
        }
        setQuery('');
        setShowDropdown(false);
    };

    const handleKeyDown = (e) => {
        if (!showDropdown || results.length === 0) {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (onProductSearch && query.trim().length >= 2) {
                    onProductSearch(query.trim());
                    navigate(`/products?search=${encodeURIComponent(query.trim())}`);
                    setQuery('');
                    setShowDropdown(false);
                }
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0) {
                handleSelect(results[selectedIndex]);
            } else if (onProductSearch && query.trim().length >= 2) {
                onProductSearch(query.trim());
                navigate(`/products?search=${encodeURIComponent(query.trim())}`);
                setQuery('');
                setShowDropdown(false);
            }
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    const clearSearch = () => {
        setQuery('');
        setResults([]);
        setShowDropdown(false);
        inputRef.current?.focus();
    };

    return (
        <div className={`relative ${className}`}>
            <div className="flex">
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onFocus={() => query.trim() && results.length > 0 && setShowDropdown(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {query && (
                    <button
                        type="button"
                        className="px-3 py-2 border border-l-0 border-gray-200 bg-gray-100 hover:bg-gray-200 focus:outline-none"
                        onClick={clearSearch}
                    >
                        <FiX />
                    </button>
                )}
                <button
                    type="button"
                    className="px-3 py-2 border border-l-0 border-gray-200 bg-primary text-white rounded-r hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
                    onClick={() => {
                        if (onProductSearch && query.trim().length >= 2) {
                            onProductSearch(query.trim());
                            navigate(`/products?search=${encodeURIComponent(query.trim())}`);
                            setQuery('');
                            setShowDropdown(false);
                        }
                    }}
                >
                    <FiSearch />
                </button>
            </div>

            {showDropdown && results.length > 0 && (
                <div
                    ref={dropdownRef}
                    className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto"
                >
                    {results.map((item, i) => {
                        const Icon = item.icon;
                        const isSelected = i === selectedIndex;
                        return (
                            <button
                                key={`${item.type}-${item.path}`}
                                className={`w-full flex items-center px-3 py-2 text-left text-sm transition-colors ${
                                    isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-gray-50 text-gray-700'
                                } ${item.type === 'product_search' ? 'border-t border-gray-100' : ''}`}
                                onClick={() => handleSelect(item)}
                                onMouseEnter={() => setSelectedIndex(i)}
                            >
                                {Icon && <Icon className={`mr-2 flex-shrink-0 ${isSelected ? 'text-primary' : 'text-gray-400'}`} />}
                                <span className="truncate">{item.label}</span>
                                {item.type === 'page' && (
                                    <span className="ml-auto text-xs text-gray-400 flex-shrink-0">{t('Page')}</span>
                                )}
                                {item.type === 'product_search' && (
                                    <span className="ml-auto text-xs text-gray-400 flex-shrink-0">{t('Products')}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default GlobalSearchDropdown;
